// Shared presentational board components + small hooks for Playtest and Duel.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import CardImage from '../components/CardImage';
import { useCardImage, useCardStats } from '../api/cardCache';
import { cardImageUrl } from '../config';
import cardBack from '../assets/card-back.jpg';
import { sliceConfig, type Inst, type Pos } from './model';

// ── a counter chip (black gradient circle, yellow number) shown bottom-left of
// a card. Draggable: drop on another allowed card to move it, or anywhere else
// to remove it. Its pointer events are isolated so grabbing it never drags the
// card underneath. ─────────────────────────────────────────────────────────
export function CounterBadge({ count, src, onInc, onDec }: { count: number; src: string; onInc?: () => void; onDec?: () => void }) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: `ctr:${src}`, data: { kind: 'counter-move', src } });
  // Fire the drag activator but stop the event bubbling to the card's own
  // listeners (mouse/touch sensors), so the badge drags on its own.
  const isolate = (name: 'onMouseDown' | 'onTouchStart') => (e: React.SyntheticEvent) => {
    e.stopPropagation();
    (listeners as Record<string, ((ev: React.SyntheticEvent) => void) | undefined>)?.[name]?.(e);
  };
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      onMouseDown={isolate('onMouseDown')}
      onTouchStart={isolate('onTouchStart')}
      onPointerDown={e => e.stopPropagation()}
      onClick={e => { e.stopPropagation(); onInc?.(); }}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onDec?.(); }}
      className="play-counter"
      style={{ opacity: isDragging ? 0.3 : 1 }}
      title="Left-click +1 · right-click −1 · drag to move"
    >
      {count}
    </div>
  );
}

/** Tracks which card uids are currently flashing a "halo" (a right-click ping to
 *  point at / target a card). `flash(uid)` shows it for ~1.6s. Shared by playtest
 *  and duel; in duel the incoming ping also calls `flash`. */
export function useHalo() {
  const [haloed, setHaloed] = useState<Set<string>>(new Set());
  const timers = useRef<Map<string, number>>(new Map());
  const flash = useCallback((uid: string) => {
    setHaloed(s => { const n = new Set(s); n.add(uid); return n; });
    const prev = timers.current.get(uid);
    if (prev) window.clearTimeout(prev);
    timers.current.set(uid, window.setTimeout(() => {
      setHaloed(s => { const n = new Set(s); n.delete(uid); return n; });
      timers.current.delete(uid);
    }, 1600));
  }, []);
  useEffect(() => () => { timers.current.forEach(t => window.clearTimeout(t)); }, []);
  return { haloed, flash };
}

/** ATK/DEF chip (white on black) shown bottom-right of a face-up monster-zone
 *  card. Non-monsters read 0/0. Shows overridden stats (green if raised, red if
 *  lowered) and animates changes like life points. Clicking opens the editor. */
function StatBadge({ name, atk, def, onClick }: { name: string; atk?: number; def?: number; onClick?: () => void }) {
  const s = useCardStats(name);
  // Hold off rendering the numbers until the card's stats have loaded. Mounting
  // the count-up only once real values are known seeds it at the correct value,
  // so a freshly-played monster doesn't animate 0 → ATK. (Stat edits still
  // animate — StatNumbers stays mounted once shown.)
  if (!s) return null;
  return <StatNumbers isMon={s.isMonster} origAtk={s.atk ?? 0} origDef={s.def ?? 0} atk={atk} def={def} onClick={onClick} />;
}

function StatNumbers({ isMon, origAtk, origDef, atk, def, onClick }: {
  isMon: boolean; origAtk: number; origDef: number; atk?: number; def?: number; onClick?: () => void;
}) {
  const baseAtk = isMon ? origAtk : 0;
  const baseDef = isMon ? origDef : 0;
  const curAtk = atk ?? baseAtk;
  const curDef = def ?? baseDef;
  const showAtk = useCountUp(curAtk);
  const showDef = useCountUp(curDef);
  const cls = (cur: number, orig: number) => (cur > orig ? ' stat-up' : cur < orig ? ' stat-down' : '');
  return (
    <span className={`play-stats${onClick ? ' clickable' : ''}`} onClick={onClick ? e => { e.stopPropagation(); onClick(); } : undefined}>
      <span className={`stat${cls(curAtk, baseAtk)}`}>{showAtk}</span>/<span className={`stat${cls(curDef, baseDef)}`}>{showDef}</span>
    </span>
  );
}

/** Editor for a monster's ATK/DEF (rendered in the duel centre / a popup). */
export function StatEditor({ name, atk, def, onConfirm, onCancel }: {
  name: string; atk?: number; def?: number;
  onConfirm: (atk: number, def: number) => void; onCancel: () => void;
}) {
  const s = useCardStats(name);
  const origAtk = s?.isMonster ? (s.atk ?? 0) : 0;
  const origDef = s?.isMonster ? (s.def ?? 0) : 0;
  const [a, setA] = useState(atk ?? origAtk);
  const [d, setD] = useState(def ?? origDef);
  const colour = (cur: number, orig: number) => (cur > orig ? 'stat-up' : cur < orig ? 'stat-down' : '');
  const field = (label: string, val: number, set: (n: number) => void, orig: number) => (
    <div className="stat-edit-field">
      <span className="stat-edit-label">{label}</span>
      <button type="button" onClick={() => set(val + 100)}>+</button>
      <input type="number" className={`stat-edit-input ${colour(val, orig)}`} value={val} onChange={e => set(Math.max(0, parseInt(e.target.value, 10) || 0))} />
      <button type="button" onClick={() => set(Math.max(0, val - 100))}>−</button>
    </div>
  );
  return (
    <div className="duel-peek stat-editor">
      <div className="pt-viewer-head"><h4>Edit stats — {name}</h4></div>
      <div className="stat-edit-body">
        <div className="stat-edit-row">
          {field('ATK', a, setA, origAtk)}
          {field('DEF', d, setD, origDef)}
        </div>
        <div className="stat-edit-actions">
          <button type="button" className="create-deck-btn" onClick={() => onConfirm(a, d)}>Confirm</button>
          <button type="button" onClick={() => onConfirm(origAtk, origDef)}>Revert to Original</button>
          <button type="button" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── draggable card ─────────────────────────────────────────────────────────
export function PlayCard({ inst, src, faceUp = true, pos = 'atk', counters = 0, atk, def, draggable = true, stats = false, halo = false, onView, onOpen, onHold, onHalo, onCounterInc, onCounterDec, onStatClick }: {
  inst: Inst; src: string; faceUp?: boolean; pos?: Pos; counters?: number; atk?: number; def?: number; draggable?: boolean; stats?: boolean; halo?: boolean;
  onView: (n: string) => void; onOpen?: (n: string) => void; onHold?: () => void; onHalo?: () => void; onCounterInc?: () => void; onCounterDec?: () => void; onStatClick?: () => void;
}) {
  // Non-draggable cards (opponent / reveal strip / peek) get a namespaced dnd id
  // so they never collide with your draggable cards' uids (uids are per-client,
  // so the same uid can exist on both boards — e.g. the skill card).
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: draggable ? inst.uid : `ro:${src}:${inst.uid}`, data: { uid: inst.uid, name: inst.name, src }, disabled: !draggable });
  const img = useCardImage(inst.name);
  // Desktop press-and-hold (stationary mouse) → onHold (position menu). A drag
  // (mouse or touch) sets didDrag so the trailing click never also opens detail.
  const holdTimer = useRef<number | undefined>(undefined);
  const held = useRef(false);
  const didDrag = useRef(false);
  const down = useRef<{ x: number; y: number } | null>(null);
  const clearHold = () => { if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = undefined; } };
  useEffect(() => { if (isDragging) { didDrag.current = true; clearHold(); } }, [isDragging]);
  const onDown = (e: React.PointerEvent) => {
    didDrag.current = false;
    if (!onHold) return;
    down.current = { x: e.clientX, y: e.clientY };
    held.current = false;
    clearHold();
    holdTimer.current = window.setTimeout(() => { held.current = true; onHold(); }, 500);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (down.current && Math.hypot(e.clientX - down.current.x, e.clientY - down.current.y) > 6) clearHold();
  };
  const endHold = () => { clearHold(); down.current = null; };
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`play-card${pos === 'def' ? ' play-def' : ''}${isDragging ? ' play-dragging' : ''}${draggable ? '' : ' play-static'}`}
      onPointerDown={onDown}
      onPointerMove={onPointerMove}
      onPointerUp={endHold}
      onPointerCancel={endHold}
      onMouseEnter={() => onView(inst.name)}
      onContextMenu={onHalo ? (e => { e.preventDefault(); e.stopPropagation(); onHalo(); }) : undefined}
      onClick={() => {
        if (held.current || didDrag.current) { held.current = false; didDrag.current = false; return; }
        (onOpen ? onOpen(inst.name) : onView(inst.name));
      }}
      title={inst.name}
    >
      {faceUp
        ? <CardImage src={cardImageUrl(img)} alt={inst.name} />
        : <img src={cardBack} className="play-cardback" alt="face-down card" />}
      {counters > 0 && <CounterBadge count={counters} src={src} onInc={onCounterInc} onDec={onCounterDec} />}
      {stats && faceUp && <StatBadge name={inst.name} atk={atk} def={def} onClick={onStatClick} />}
      {halo && <span className="play-halo" aria-hidden="true" />}
    </div>
  );
}

export function Zone({ id, className, label, onClick, onContext, children }: {
  id: string; className?: string; label?: string; onClick?: () => void; onContext?: (e: React.MouseEvent) => void; children?: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} data-zoneid={id} className={`pt-zone ${className ?? ''}${isOver ? ' pt-zone-over' : ''}`} onClick={onClick} onContextMenu={onContext}>
      {label && <span className="pt-zone-label">{label}</span>}
      {children}
    </div>
  );
}

/** A clickable preview of what a card looks like in a given position. */
export function PosOption({ name, faceUp, pos, label, onClick }: {
  name: string; faceUp: boolean; pos: Pos; label: string; onClick: () => void;
}) {
  const img = useCardImage(name);
  return (
    <button type="button" className="pt-pos-opt" onClick={onClick} title={label}>
      <div className="pt-pos-cardbox">
        <div className={`pt-pos-card${pos === 'def' ? ' def' : ''}`}>
          {faceUp ? <CardImage src={cardImageUrl(img)} alt={label} /> : <img src={cardBack} alt="face-down" />}
        </div>
      </div>
    </button>
  );
}

/** Slices drawn over the hovered zone during a drag. `prevSlice` flags where a
 *  card being re-positioned currently sits (-1 if not applicable). */
export function SliceOverlay({ hint, prevSlice }: { hint: { zone: string; slice: number }; prevSlice: number }) {
  const el = document.querySelector(`[data-zoneid="${hint.zone}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const { labels, clips, align } = sliceConfig(hint.zone);
  return (
    <div className="pt-slice" style={{ left: r.left, top: r.top, width: r.width, height: r.height }}>
      {labels.map((label, i) => {
        const active = i === hint.slice;
        const prev = i === prevSlice;
        // The old spot is always amber-marked; hovering it just brightens that
        // amber (rather than turning blue) so it reads as "return to current
        // position" — the drag is a no-op there, so the label says CANCEL.
        const cls = `${active && !prev ? ' pt-slice-on' : ''}${prev ? ' pt-slice-prev' : ''}${active && prev ? ' pt-slice-prev-on' : ''}`;
        return (
          <div key={i} className={`pt-slice-cell${cls}`} style={{ clipPath: clips[i], ...align[i] }}>
            <span className="pt-slice-label">{active && prev ? 'Cancel' : label}</span>
          </div>
        );
      })}
    </div>
  );
}

/** A brief riffle animation shown over the deck when it's shuffled. */
export function ShuffleFx() {
  return (
    <div className="pt-shuffle-fx" aria-hidden="true">
      <span className="pt-shuffle-card sc1" style={{ backgroundImage: `url(${cardBack})` }} />
      <span className="pt-shuffle-card sc2" style={{ backgroundImage: `url(${cardBack})` }} />
      <span className="pt-shuffle-card sc3" style={{ backgroundImage: `url(${cardBack})` }} />
    </div>
  );
}

export function OverlayImg({ name }: { name: string }) {
  const img = useCardImage(name);
  return <CardImage src={cardImageUrl(img)} alt={name} />;
}

/** Animate a displayed number toward `target`. It moves at ~1 unit/ms for small
 *  changes but the whole animation is capped at 1s, so big swings never crawl. */
export function useCountUp(target: number): number {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);   // live value, so a new target continues from here
  const rafRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return;
    const duration = Math.min(1000, Math.abs(target - from));   // ~1 unit/ms, capped at 1s
    let startT: number | undefined;
    const step = (t: number) => {
      if (startT === undefined) startT = t;
      const p = duration <= 0 ? 1 : Math.min(1, (t - startT) / duration);
      const val = from + (target - from) * p;
      fromRef.current = p < 1 ? val : target;
      setDisplay(Math.round(fromRef.current));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target]);
  return display;
}

/** The horizontally-scrolling pile viewer strip. A vertical mouse wheel scrolls
 *  it sideways (via a non-passive listener so the page doesn't scroll instead),
 *  and the ‹ › buttons nudge it for trackpads/touch. */
export function ViewerStrip({ cards, src, draggable = true, onView, onToHand }: {
  cards: Inst[]; src: string; draggable?: boolean; onView: (n: string) => void; onToHand?: (uid: string) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  const nudge = (dx: number) => stripRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  return (
    <div className="pt-viewer-scroll">
      <button type="button" className="pt-scroll-btn" onClick={() => nudge(-220)} aria-label="Scroll left">‹</button>
      <div className="pt-viewer-strip" ref={stripRef}>
        {cards.map(c => (
          <PlayCard key={c.uid} inst={c} src={src} draggable={draggable} onView={onView}
            onOpen={onToHand ? () => onToHand(c.uid) : onView} />
        ))}
      </div>
      <button type="button" className="pt-scroll-btn" onClick={() => nudge(220)} aria-label="Scroll right">›</button>
    </div>
  );
}

/** The player's own hand — a droppable row of draggable cards, with an optional
 *  ⋯ menu slot in the header. */
export function HandZone({ hand, menu, halo, onView, onOpen, picking = false, onPick }: { hand: Inst[]; menu?: React.ReactNode; halo?: (uid: string) => boolean; onView: (n: string) => void; onOpen: (n: string) => void; picking?: boolean; onPick?: (inst: Inst) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'hand' });
  const cardsRef = useRef<HTMLDivElement>(null);
  // A vertical mouse wheel scrolls the hand sideways (like the pile viewer), but
  // only when it can actually scroll — otherwise the wheel scrolls the page.
  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0 || el.scrollWidth <= el.clientWidth) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  return (
    <div ref={setNodeRef} className={`pt-hand${isOver ? ' pt-zone-over' : ''}${picking ? ' pt-hand-picking' : ''}`}>
      <div className="pt-hand-head"><span className="pt-zone-label pt-hand-label">{picking ? 'Reveal which card? (click one)' : `Hand (${hand.length})`}</span>{menu}</div>
      <div className="pt-hand-cards" ref={cardsRef}>
        {hand.length === 0 && <span className="muted pt-empty">No cards in hand.</span>}
        {hand.map(c => <PlayCard key={c.uid} inst={c} src="hand" halo={halo?.(c.uid)} onView={onView} onOpen={picking && onPick ? () => onPick(c) : onOpen} />)}
      </div>
    </div>
  );
}

/** A ⋯ button that opens a small dropdown of labelled actions. */
export function DotMenu({ items, up = false }: { items: { label: string; onClick: () => void }[]; up?: boolean }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const place = () => { const b = btnRef.current?.getBoundingClientRect(); if (b) setPos({ left: b.right, top: up ? b.top : b.bottom }); };
    place();
    const onDoc = (e: MouseEvent) => { if (!btnRef.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDoc);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); document.removeEventListener('mousedown', onDoc); };
  }, [open, up]);
  return (
    <div className="dot-menu">
      <button ref={btnRef} type="button" className="dot-menu-btn" onClick={e => { e.stopPropagation(); setOpen(o => !o); }} title="More actions">⋯</button>
      {/* Portaled to body so scroll/overflow ancestors (the hand) can't clip it. */}
      {open && createPortal(
        <div ref={listRef} className="dot-menu-list dot-menu-portal" style={{ left: pos.left, top: pos.top, transform: up ? 'translate(-100%, -100%)' : 'translate(-100%, 0)' }}>
          {items.map((it, i) => <button key={i} type="button" onClick={e => { e.stopPropagation(); setOpen(false); it.onClick(); }}>{it.label}</button>)}
        </div>, document.body)}
    </div>
  );
}

/** Floating viewer for "reveal/look at top N": cards left→right = top→bottom of
 *  the deck. Drag a card to reorder; closing returns them in that order. */
export function DeckPeekViewer({ cards, reveal, onReorder, onClose, onView }: {
  cards: Inst[]; reveal: boolean; onReorder: (from: number, to: number) => void; onClose: () => void; onView: (n: string) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ from: number; moved: boolean } | null>(null);
  const [fromI, setFromI] = useState<number | null>(null);
  const [overI, setOverI] = useState<number | null>(null);

  // Insertion index (0..N) for the current pointer x among the .peek-card tiles.
  const targetIndex = (clientX: number) => {
    const strip = stripRef.current; if (!strip) return cards.length;
    const els = Array.from(strip.querySelectorAll<HTMLElement>('.peek-card'));
    for (let k = 0; k < els.length; k++) {
      const r = els[k].getBoundingClientRect();
      if (clientX < r.left + r.width / 2) return k;
    }
    return els.length;
  };
  const down = (e: React.PointerEvent, i: number) => { dragRef.current = { from: i, moved: false }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e: React.PointerEvent) => {
    const d = dragRef.current; if (!d) return;
    if (!d.moved) { d.moved = true; setFromI(d.from); }
    setOverI(targetIndex(e.clientX));
  };
  const up = () => {
    const d = dragRef.current; dragRef.current = null;
    if (d && d.moved && overI != null) {
      const to = overI > d.from ? overI - 1 : overI;      // account for the removal shift
      if (to !== d.from && to >= 0 && to < cards.length) onReorder(d.from, to);
    }
    setFromI(null); setOverI(null);
  };

  return (
    <div className="duel-peek">
      <div className="pt-viewer-head">
        <h4>{reveal ? 'Revealing' : 'Looking at'} top {cards.length}{cards.length > 1 ? '  ·  drag to reorder (◀ top · bottom ▶)' : ''}</h4>
        <button type="button" className="preview-close" onClick={onClose} title="Return to deck">✕</button>
      </div>
      <div className="pt-viewer-scroll">
        <div className="pt-viewer-strip peek-strip" ref={stripRef}>
          {cards.map((c, i) => (
            <div
              key={c.uid}
              className={`peek-card peek-reorder${fromI === i ? ' peek-dragging' : ''}${fromI != null && fromI !== i && overI === i ? ' peek-over' : ''}`}
              onPointerDown={e => down(e, i)} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
            >
              <PlayCard inst={c} src="peek" draggable={false} onView={onView} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Reveal-any-N viewer: shows the whole deck; click cards to select up to N,
 *  then ✓ to reveal the picks (or ✕ to cancel). */
export function RevealSelectViewer({ cards, n, selected, onToggle, onConfirm, onCancel, onView }: {
  cards: Inst[]; n: number; selected: string[]; onToggle: (uid: string) => void;
  onConfirm: () => void; onCancel: () => void; onView: (name: string) => void;
}) {
  return (
    <div className="duel-peek">
      <div className="pt-viewer-head">
        <h4>Reveal cards — pick up to {n} ({selected.length} selected)</h4>
        <div className="reveal-sel-actions">
          <button type="button" className="reveal-sel-ok" disabled={selected.length === 0} onClick={onConfirm} title="Reveal selected">✓</button>
          <button type="button" className="preview-close" onClick={onCancel} title="Cancel">✕</button>
        </div>
      </div>
      <div className="pt-viewer-scroll">
        <div className="pt-viewer-strip peek-strip">
          {cards.map(c => (
            <div key={c.uid} className={`peek-card reveal-sel-card${selected.includes(c.uid) ? ' selected' : ''}`} onClick={() => onToggle(c.uid)}>
              <PlayCard inst={c} src="peek" draggable={false} onView={onView} />
              {selected.includes(c.uid) && <span className="reveal-sel-check" aria-hidden="true">✓</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A draggable "counter" chip — drag it onto a card in a monster, spell/trap, or
 *  field-spell zone to place a counter there. */
export function CounterSource() {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id: 'counter-src', data: { kind: 'counter-add' } });
  return (
    <div className="pt-counter-panel">
      <div className="pt-counter-src">
        {/* Only the chip is the drag node, so the drag preview is chip-sized. */}
        <span
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          className={`play-counter pt-counter-demo pt-counter-grab${isDragging ? ' dragging' : ''}`}
          title="Drag onto a card to add a counter"
        >＋</span>
        <span className="pt-counter-label">Drag this counter onto a card</span>
      </div>
      <p className="muted pt-counter-hint">On a counter: left-click +1, right-click −1, drag to move (or drag off a card to remove).</p>
    </div>
  );
}
